import { useEffect, useState, useCallback } from "react";
import { useGetCurrentUser } from "@workspace/api-client-react";
import { useRecentLogs } from "@/hooks/use-recent-logs";

const SUFFIX_DISMISSED = "gettingStartedDismissed";
const SUFFIX_TRIED_PLATE_TALK = "triedPlateTalk";
const SUFFIX_VIEWED_HYDRATION = "viewedHydration";
const SUFFIX_FIRST_LOG_CELEBRATED = "firstLogCelebrated";
const SUFFIX_LAST_KNOWN_LOG_COUNT = "lastKnownLogCountIsZero";

export type ActivationStepId =
  | "profile"
  | "firstLog"
  | "plateTalk"
  | "calorieTarget"
  | "hydration";

export interface ActivationStep {
  id: ActivationStepId;
  done: boolean;
  href: string;
}

function userKey(uid: string | null | undefined, suffix: string) {
  return `plateOS:${uid ?? "anon"}:${suffix}`;
}

function readFlag(key: string) {
  try { return localStorage.getItem(key) === "1"; } catch { return false; }
}
function writeFlag(key: string, val: boolean) {
  try { localStorage.setItem(key, val ? "1" : "0"); } catch {}
}

let CURRENT_UID: string | null = null;

function fire() { window.dispatchEvent(new Event("plateOS:activation")); }

export function markPlateTalkVisited() {
  writeFlag(userKey(CURRENT_UID, SUFFIX_TRIED_PLATE_TALK), true);
  fire();
}
export function markHydrationViewed() {
  writeFlag(userKey(CURRENT_UID, SUFFIX_VIEWED_HYDRATION), true);
  fire();
}
export function getFirstLogCelebrated(uid?: string | null) {
  return readFlag(userKey(uid ?? CURRENT_UID, SUFFIX_FIRST_LOG_CELEBRATED));
}
export function markFirstLogCelebrated(uid?: string | null) {
  writeFlag(userKey(uid ?? CURRENT_UID, SUFFIX_FIRST_LOG_CELEBRATED), true);
}
export function getKnownEmptyLogState(uid: string | null | undefined) {
  return readFlag(userKey(uid, SUFFIX_LAST_KNOWN_LOG_COUNT));
}
export function setKnownEmptyLogState(uid: string | null | undefined, isEmpty: boolean) {
  writeFlag(userKey(uid, SUFFIX_LAST_KNOWN_LOG_COUNT), isEmpty);
}

export function useActivationStatus() {
  const { data: user } = useGetCurrentUser();
  const { data: recent } = useRecentLogs(1);

  const uid = (user as any)?.email ?? null;
  CURRENT_UID = uid;

  const [tick, setTick] = useState(0);
  useEffect(() => {
    const handler = () => setTick(t => t + 1);
    window.addEventListener("plateOS:activation", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("plateOS:activation", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);
  void tick;

  const dismissed = readFlag(userKey(uid, SUFFIX_DISMISSED));
  const triedPlateTalk = readFlag(userKey(uid, SUFFIX_TRIED_PLATE_TALK));
  const viewedHydration = readFlag(userKey(uid, SUFFIX_VIEWED_HYDRATION));

  const profileDone = !!(user && user.heightCm && user.weightKg && user.activityLevel);
  const firstLogDone = !!recent && recent.length > 0;
  const calorieTargetDone = !!(user && user.dailyCalorieTarget && user.dailyCalorieTarget > 0);

  const steps: ActivationStep[] = [
    { id: "profile",       done: profileDone,        href: "/profile" },
    { id: "firstLog",      done: firstLogDone,       href: "/plate-log" },
    { id: "plateTalk",     done: triedPlateTalk,     href: "/plate-talk" },
    { id: "calorieTarget", done: calorieTargetDone,  href: "/profile" },
    { id: "hydration",     done: viewedHydration,    href: "/dashboard" },
  ];

  const completedCount = steps.filter(s => s.done).length;
  const totalCount = steps.length;
  const allDone = completedCount === totalCount;

  const dismiss = useCallback(() => {
    writeFlag(userKey(uid, SUFFIX_DISMISSED), true);
    fire();
  }, [uid]);

  const visible = !dismissed && !allDone && !!user;

  return { steps, completedCount, totalCount, allDone, visible, dismiss };
}

/**
 * Hook that auto-marks plateTalk + hydration steps when those routes are visited
 * (regardless of how the user navigated there — direct link, CTA, etc).
 */
export function useTrackRouteForActivation(location: string) {
  useEffect(() => {
    if (location === "/plate-talk") markPlateTalkVisited();
    if (location === "/dashboard") markHydrationViewed();
  }, [location]);
}
